/**
 * Local-dev sentinel. When the running bundle reports this version the
 * app-version feature is disabled: there is nothing meaningful to compare
 * against and no `/version.txt` is served during local development.
 */
export const DEV_VERSION = 'dev';

/**
 * Parse the leading `yyyyMMdd` of an Azure DevOps build number
 * (e.g. `20260708.3`) into a UTC Date at midnight. Returns `null` when the
 * prefix is not a real calendar date.
 */
export function parseBuildDate(version: string): Date | null {
  const match = version.trim().match(/^(\d{4})(\d{2})(\d{2})/);

  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

  // Reject values like `20261332` that the Date constructor would roll over.
  const isRealDate = date.getUTCFullYear() === Number(year)
    && date.getUTCMonth() === Number(month) - 1
    && date.getUTCDate() === Number(day);

  return isRealDate ? date : null;
}

/**
 * Whole days the running build (`current`) is behind the deployed build
 * (`latest`), derived from the `yyyyMMdd` prefix of each build number.
 * Returns `null` when either date cannot be parsed. Never negative.
 */
export function daysBehind(current: string, latest: string): number | null {
  const currentDate = parseBuildDate(current);
  const latestDate = parseBuildDate(latest);

  if (!currentDate || !latestDate) {
    return null;
  }

  const diffMs = latestDate.getTime() - currentDate.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return Math.max(0, days);
}

/**
 * True when `latest` (fetched from `/version.txt`) is a different, non-empty
 * build than the running `current` build. The `dev` sentinel is never "new".
 */
export function isNewVersion(current: string, latest: string): boolean {
  const currentTrimmed = current.trim();
  const latestTrimmed = latest.trim();

  if (!latestTrimmed || currentTrimmed === DEV_VERSION) {
    return false;
  }

  return latestTrimmed !== currentTrimmed;
}
